import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MdGroups, MdMedicalServices, MdCheckCircle, MdTimer,
  MdPrint, MdAdd, MdDelete, MdScience,
  MdStickyNote2, MdLocalHospital, MdPhone,
} from 'react-icons/md';
import { PageShell, Badge, DataTable, StatCard } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { useEmrSettings } from '../../hooks/useEmrSettings';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';
import { Q, queueRowId } from '../../config/queueStatus';
import { buildIpdAdmitLink } from '../../utils/opIpLink';
import { buildEmrPayload, rxPadToEmrForm, buildLabTests, buildPharmacyLines, buildPrescriptionText } from '../../utils/emrHelpers';
import MedicalCertificateModal from '../../components/emr/MedicalCertificateModal';
import { printPrescription } from '../../utils/hospitalPdf';

import { doctorQueueMatch } from '../../utils/doctorMatch';
function waitLabel(row) {
  if (!row.createdAt) return null;
  const mins = Math.floor((Date.now() - new Date(row.createdAt).getTime()) / 60000);
  if (Number.isNaN(mins) || mins < 0) return null;
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

/* ─── Prescription pad ─── */
const FREQ_OPTIONS = ['Once daily', 'Twice daily', 'Three times daily', 'Four times daily', 'Every 6 hours', 'Every 8 hours', 'Before meals', 'After meals', 'At bedtime', 'SOS'];
const DUR_OPTIONS = ['1 day', '2 days', '3 days', '5 days', '7 days', '10 days', '14 days', '1 month', 'Ongoing'];
const ROUTE_OPTIONS = ['Oral', 'IV', 'IM', 'SC', 'Topical', 'Inhaled', 'Sublingual', 'Rectal'];

const COMMON_DIAGNOSES = [
  'Fever', 'Viral fever', 'Hypertension', 'Diabetes mellitus type 2', 'Upper respiratory tract infection',
  'Acute gastroenteritis', 'Bronchial asthma', 'Urinary tract infection', 'Anemia',
  'Hypothyroidism', 'Arthritis', 'Low back pain', 'Migraine', 'Anxiety disorder',
  'Dengue fever', 'Malaria', 'COVID-19', 'Pneumonia', 'Myocardial infarction',
];

function PrescriptionPad({ patient, onClose, onSaved, hospital }) {
  const { user } = useAuth();
  const { settings: emrSettings, genieMeds, error: emrSettingsError } = useEmrSettings();
  const diagTemplates = [...(emrSettings.sectionTemplates?.diagnosis || []), ...COMMON_DIAGNOSES];
  const defaultDur = emrSettings.medSectionConfig?.defaultDuration || '5 days';
  const { data: catData } = useLiveData(() => ipApi.getLabCatalog(), []);
  const { data: invData } = useLiveData(() => ipApi.getPharmacyInventory(), []);
  const labCatalog = catData?.items || [];
  const inventory = invData?.items || invData?.inventory || [];
  const pv = patient.preVitals || {};
  const [vitals, setVitals] = useState({
    bp: pv.bp || '', pulse: pv.pulse || '', temp: pv.temp || '', spo2: pv.spo2 || '', weight: pv.weight || '', rr: pv.rr || '',
  });
  const [diagnoses, setDiagnoses] = useState([]);
  const [diagInput, setDiagInput] = useState('');
  const [diagSuggestions, setDiagSuggestions] = useState([]);
  const [meds, setMeds] = useState([
    { name: '', dose: '', route: 'Oral', freq: 'Twice daily', dur: defaultDur, note: '' },
  ]);
  const [advice, setAdvice] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [labTests, setLabTests] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const addMed = () => setMeds((m) => [...m, { name: '', dose: '', route: 'Oral', freq: 'Twice daily', dur: defaultDur, note: '' }]);
  const removeMed = (i) => setMeds((m) => m.filter((_, j) => j !== i));
  const setMed = (i, patch) => setMeds((m) => m.map((x, j) => j === i ? { ...x, ...patch } : x));

  const addDiag = (d) => { if (d && !diagnoses.includes(d)) setDiagnoses((ds) => [...ds, d]); setDiagInput(''); setDiagSuggestions([]); };
  const removeDiag = (d) => setDiagnoses((ds) => ds.filter((x) => x !== d));

  const handleDiagInput = (val) => {
    setDiagInput(val);
    if (val.length > 1) {
      setDiagSuggestions(diagTemplates.filter((d) => d.toLowerCase().includes(val.toLowerCase())).slice(0, 5));
    } else {
      setDiagSuggestions([]);
    }
  };

  const printRx = () => {
    const form = rxPadToEmrForm(patient, { diagnoses, meds, vitals, advice, followUp, labTests });
    printPrescription(form, user, hospital || { name: user?.hospital }, user, emrSettings);
  };

  const save = async () => {
    if (!diagnoses.length && !meds.filter((m) => m.name).length) {
      setMsg('Add at least one diagnosis or medicine before saving.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      const recordId = `EMR-${Date.now()}`;
      const form = rxPadToEmrForm(patient, { diagnoses, meds, vitals, advice, followUp, labTests });
      const payload = buildEmrPayload(form, user, queueRowId(patient), recordId, new Date().toISOString());
      payload.status = 'completed';
      await ipApi.saveEmr(payload);

      const labNames = form.labOrders || [];
      if (labNames.length) {
        await ipApi.createLabOrder({
          patientName: form.patientName,
          patientId: form.patientId,
          tests: buildLabTests(labNames, labCatalog),
          referredBy: user?.name,
          hospitalId: user.hospitalId,
          recordId,
        });
      }

      const rxLines = (form.rxLines || []).filter((r) => r.drug);
      if (rxLines.length) {
        const lines = buildPharmacyLines(rxLines, inventory);
        await ipApi.createPharmacyOrder({
          patientName: form.patientName,
          patientId: form.patientId,
          doctorName: user?.name,
          items: buildPrescriptionText(rxLines),
          lines,
          rxLines,
          prescription: buildPrescriptionText(rxLines),
          hospitalId: user.hospitalId,
          status: 'pending',
          source: 'doctor_queue',
          recordId,
        });
      }

      const qid = queueRowId(patient);
      if (qid && patient.status !== Q.COMPLETED) {
        await ipApi.patchQueue(qid, Q.COMPLETED);
      }

      setMsg('Visit saved to EMR and marked complete.');
      if (onSaved) await onSaved({ diagnoses, meds, vitals, advice, followUp, labTests });
    } catch (e) {
      setMsg(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rx-pad card mb">
      {/* Header */}
      <div className="rx-head">
        <div>
          <div className="rx-patient-name">{patient.name || patient.patientName}</div>
          <div className="rx-patient-meta">
            {patient.patientId && <span>UHID: {patient.patientId}</span>}
            {patient.age && <span> · Age: {patient.age} {patient.gender || ''}</span>}
            {patient.reason && <span> · {patient.reason}</span>}
          </div>
        </div>
        <div className="row-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={printRx}><MdPrint /> Print Rx</button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>✕ Close</button>
        </div>
      </div>

      {emrSettingsError && (
        <div className="error mb" style={{ margin: '0 16px 12px' }}>
          Rx pad settings unavailable — using defaults. ({emrSettingsError})
        </div>
      )}

      {/* Vitals */}
      <div className="rx-section">
        <div className="rx-section-title"><MdLocalHospital /> Quick Vitals</div>
        <div className="rx-vitals-row">
          {[
            { key: 'bp', label: 'BP', ph: '120/80' },
            { key: 'pulse', label: 'Pulse', ph: '72 bpm' },
            { key: 'temp', label: 'Temp °F', ph: '98.4' },
            { key: 'spo2', label: 'SpO₂ %', ph: '98' },
            { key: 'weight', label: 'Weight kg', ph: '65' },
            { key: 'rr', label: 'Resp Rate', ph: '18' },
          ].map(({ key, label, ph }) => (
            <div key={key} className="rx-vital-field">
              <label>{label}</label>
              <input className="input input-sm" placeholder={ph} value={vitals[key]} onChange={(e) => setVitals({ ...vitals, [key]: e.target.value })} />
            </div>
          ))}
        </div>
      </div>

      {/* Diagnosis */}
      <div className="rx-section">
        <div className="rx-section-title"><MdStickyNote2 /> Diagnosis / Impression</div>
        <div className="rx-diag-row">
          <div style={{ position: 'relative', flex: 1 }}>
            <input
              className="input"
              placeholder="Type diagnosis (e.g. Fever, Hypertension)…"
              value={diagInput}
              onChange={(e) => handleDiagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addDiag(diagInput); }}
            />
            {diagSuggestions.length > 0 && (
              <div className="rx-diag-suggestions">
                {diagSuggestions.map((s) => (
                  <button key={s} type="button" className="rx-diag-sug-item" onClick={() => addDiag(s)}>{s}</button>
                ))}
              </div>
            )}
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => addDiag(diagInput)}>Add</button>
        </div>
        <div className="rx-diag-chips">
          {diagnoses.map((d) => (
            <span key={d} className="rx-diag-chip">
              {d}
              <button type="button" onClick={() => removeDiag(d)}>×</button>
            </span>
          ))}
        </div>
      </div>

      {/* Prescription */}
      <div className="rx-section">
        <div className="rx-section-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span><span className="rx-symbol-sm">℞</span> Prescription</span>
          <button type="button" className="btn btn-sm btn-secondary" onClick={addMed}><MdAdd /> Add medicine</button>
        </div>
        {genieMeds.length > 0 && (
          <div className="emr-template-row mb" style={{ marginBottom: 8 }}>
            {genieMeds.map((g) => (
              <button key={g} type="button" className="emr-chip" onClick={() => {
                const empty = meds.findIndex((m) => !m.name);
                if (empty >= 0) setMed(empty, { name: g });
                else setMeds((list) => [...list, { name: g, dose: '', route: 'Oral', freq: 'Twice daily', dur: defaultDur, note: '' }]);
              }}>{g}</button>
            ))}
          </div>
        )}
        <div className="rx-meds-list">
          {meds.map((m, i) => (
            <div key={i} className="rx-med-row">
              <div className="rx-med-num">{i + 1}</div>
              <div className="rx-med-fields">
                <input className="input" placeholder="Medicine name (e.g. Tab. Paracetamol 500mg)" value={m.name} onChange={(e) => setMed(i, { name: e.target.value })} style={{ flex: 2 }} />
                <input className="input input-sm" placeholder="Dose" value={m.dose} onChange={(e) => setMed(i, { dose: e.target.value })} style={{ width: 90 }} />
                <select className="input input-sm" value={m.route} onChange={(e) => setMed(i, { route: e.target.value })} style={{ width: 100 }}>
                  {ROUTE_OPTIONS.map((r) => <option key={r}>{r}</option>)}
                </select>
                <select className="input input-sm" value={m.freq} onChange={(e) => setMed(i, { freq: e.target.value })} style={{ width: 150 }}>
                  {FREQ_OPTIONS.map((f) => <option key={f}>{f}</option>)}
                </select>
                <select className="input input-sm" value={m.dur} onChange={(e) => setMed(i, { dur: e.target.value })} style={{ width: 110 }}>
                  {DUR_OPTIONS.map((d) => <option key={d}>{d}</option>)}
                </select>
                <input className="input input-sm" placeholder="Special instructions" value={m.note} onChange={(e) => setMed(i, { note: e.target.value })} style={{ flex: 1 }} />
                {meds.length > 1 && (
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => removeMed(i)}><MdDelete /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lab Tests */}
      <div className="rx-section">
        <div className="rx-section-title"><MdScience /> Investigations Advised</div>
        <input className="input" placeholder="e.g. CBC, LFT, KFT, HbA1c, Urine R/M, CXR…" value={labTests} onChange={(e) => setLabTests(e.target.value)} />
      </div>

      {/* Advice + Follow-up */}
      <div className="form-grid">
        <div className="rx-section" style={{ gridColumn: '1/-1' }}>
          <div className="rx-section-title">Advice & Instructions</div>
          <textarea className="input" rows={2} placeholder="Rest, diet modifications, precautions…" value={advice} onChange={(e) => setAdvice(e.target.value)} />
        </div>
        <div className="rx-section">
          <div className="rx-section-title">Follow-up Date</div>
          <input className="input" type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} />
        </div>
      </div>

      {/* Footer actions */}
      {msg && <div className={`mb mt ${msg.includes('saved') ? 'info-banner' : 'error'}`}>{msg}</div>}
      <div className="form-actions" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-secondary" onClick={printRx}><MdPrint /> Print Rx</button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={save}>
          {busy ? 'Saving…' : '✓ Save & Complete'}
        </button>
      </div>
    </div>
  );
}

export default function DoctorQueue() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hospital } = useHospitalProfile();
  const { settings: emrSettings, error: emrSettingsError, reload: reloadEmrSettings } = useEmrSettings();
  const docTemplates = emrSettings.docTemplates || [];
  const { data, loading, error, reload } = useLiveData(() => ipApi.getQueue({ limit: 80 }), []);
  const [busy, setBusy] = useState('');
  const [tab, setTab] = useState('waiting');
  const [rxPatient, setRxPatient] = useState(null);
  const [certPatient, setCertPatient] = useState(null);

  const all = (data?.queue || []).filter((p) => doctorQueueMatch(p, user));
  const waiting = all.filter((p) => p.status === Q.WAITING_DOCTOR);
  const consulting = all.filter((p) => p.status === Q.IN_CONSULTATION);
  const completed = all.filter((p) => p.status === Q.COMPLETED);

  const rows = (tab === 'waiting' ? waiting : tab === 'consulting' ? consulting : tab === 'done' ? completed : all)
    .map((r) => ({ ...r, id: queueRowId(r), name: r.patientName || r.name }));

  const startConsult = async (row) => {
    const id = queueRowId(row);
    setBusy(id);
    try {
      await ipApi.patchQueue(id, Q.IN_CONSULTATION);
      await reload();
    } finally {
      setBusy('');
    }
  };

  const completeConsult = async (row) => {
    if (!window.confirm('Mark visit complete without opening full EMR? Use Prescribe for diagnosis, Rx, and lab orders.')) return;
    const id = queueRowId(row);
    setBusy(id);
    try {
      await ipApi.patchQueue(id, Q.COMPLETED);
      await reload();
    } catch (e) {
      window.alert(e.message || 'Could not complete visit');
    } finally {
      setBusy('');
    }
  };

  const columns = [
    { key: 'token', label: 'Token', render: (r) => <span className="token-pill">{r.token || '—'}</span> },
    {
      key: 'name', label: 'Patient',
      render: (r) => (
        <div className="cell-stack">
          <span className="cell-strong">{r.name}</span>
          <span className="cell-sub">
            {r.patientId || 'Walk-in'}
            {r.age && ` · ${r.age}y ${r.gender || ''}`}
            {r.patientPhone && (
              <a href={`tel:${r.patientPhone}`} className="cell-phone" onClick={(e) => e.stopPropagation()}>
                <MdPhone style={{ verticalAlign: '-2px', fontSize: 12 }} /> {r.patientPhone}
              </a>
            )}
          </span>
        </div>
      ),
    },
    { key: 'reason', label: 'Chief Complaint', render: (r) => r.reason ? <span title={r.reason}>{r.reason}</span> : <span className="hint">—</span> },
    {
      key: 'time', label: 'Waiting',
      render: (r) => (
        <div className="cell-stack">
          <span>{r.time || '—'}</span>
          {waitLabel(r) && r.status !== Q.COMPLETED && (
            <span className="cell-sub" style={{ color: parseInt(waitLabel(r)) > 30 ? '#ef4444' : '#64748b' }}>
              {waitLabel(r)} wait
            </span>
          )}
        </div>
      ),
    },
    { key: 'status', label: 'Status', render: (r) => <Badge tone={r.status === Q.WAITING_DOCTOR ? 'success' : r.status === Q.COMPLETED ? 'default' : 'warning'}>{r.status}</Badge> },
    {
      key: 'actions', label: 'Actions',
      render: (r) => (
        <div className="row-actions">
          {r.status === Q.WAITING_DOCTOR && (
            <button type="button" className="btn btn-sm btn-secondary" disabled={busy === queueRowId(r)} onClick={() => startConsult(r)}>
              Start
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            title="Issue Medical Certificate"
            onClick={() => setCertPatient(r)}
          >
            📄 Certificate
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => setRxPatient(r)}
          >
            {r.status === Q.COMPLETED ? '📋 View Rx' : '✍ Prescribe'}
          </button>
          {(r.status === Q.IN_CONSULTATION || r.status === Q.WAITING_DOCTOR) && (
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              title="Refer patient for inpatient admission"
              onClick={() => navigate(buildIpdAdmitLink({
                patientName: r.name || r.patientName,
                patientId: r.patientId,
                queueId: queueRowId(r),
                doctor: r.doctor || user?.name,
                reason: r.reason,
                phone: r.patientPhone,
                visitType: r.visitType,
              }))}
            >
              🏥 Admit IPD
            </button>
          )}
          {r.status === Q.IN_CONSULTATION && (
            <button type="button" className="btn btn-sm btn-secondary" disabled={busy === queueRowId(r)} onClick={() => completeConsult(r)} title="Prefer Prescribe for full EMR">
              ✓ Quick done
            </button>
          )}
          <Link
            to={`/hms/emr?patient=${encodeURIComponent(r.patientName || r.name)}&patientId=${encodeURIComponent(r.patientId || '')}&queue=${queueRowId(r)}&doctor=${encodeURIComponent(r.doctor || user?.name || '')}`}
            className="btn btn-sm btn-secondary"
          >
            EMR
          </Link>
        </div>
      ),
    },
  ];

  return (
    <PageShell
      title="My OPD"
      subtitle="Consultation queue — prescribe, record vitals, complete"
      actions={
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => reload()}>Refresh</button>
      }
    >
      {error && <div className="error mb">{error}</div>}
      {emrSettingsError && (
        <div className="error mb">
          Could not load Rx pad settings — {emrSettingsError}{' '}
          <button type="button" className="btn btn-sm btn-secondary" onClick={reloadEmrSettings}>Retry</button>
        </div>
      )}

      {/* Prescription pad slide-in */}
      {rxPatient && (
        <PrescriptionPad
          patient={rxPatient}
          hospital={hospital}
          onClose={() => setRxPatient(null)}
          onSaved={async () => { setRxPatient(null); await reload(); }}
        />
      )}

      {/* Medical Certificate Modal */}
      {certPatient && (
        <MedicalCertificateModal
          form={{
            patientName: certPatient.patientName || certPatient.name,
            patientId: certPatient.patientId,
            patientAge: certPatient.age,
            patientGender: certPatient.gender,
          }}
          doctor={user}
          hospital={hospital}
          docTemplates={docTemplates}
          persist
          onClose={() => setCertPatient(null)}
        />
      )}

      <div className="stat-grid">
        <StatCard label="Waiting" value={waiting.length} accent="#f59e0b" icon={MdGroups} loading={loading} />
        <StatCard label="In Consultation" value={consulting.length} accent="#8b5cf6" icon={MdMedicalServices} loading={loading} />
        <StatCard label="Completed Today" value={completed.length} accent="#10b981" icon={MdCheckCircle} loading={loading} />
        <StatCard label="Total Today" value={all.length} accent="#4f8ef7" icon={MdTimer} loading={loading} />
      </div>

      <div className="period-tabs card mb">
        <button type="button" className={`emr-chip ${tab === 'waiting' ? 'active' : ''}`} onClick={() => setTab('waiting')}>
          Waiting ({waiting.length})
        </button>
        <button type="button" className={`emr-chip ${tab === 'consulting' ? 'active' : ''}`} onClick={() => setTab('consulting')}>
          In Consultation ({consulting.length})
        </button>
        <button type="button" className={`emr-chip ${tab === 'done' ? 'active' : ''}`} onClick={() => setTab('done')}>
          Completed ({completed.length})
        </button>
        <button type="button" className={`emr-chip ${tab === 'all' ? 'active' : ''}`} onClick={() => setTab('all')}>
          All ({all.length})
        </button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField="id"
        loading={loading}
        emptyMessage={tab === 'waiting' ? 'No patients waiting — well done!' : 'No patients in this category.'}
      />
    </PageShell>
  );
}
